/* global StorageEvent */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { buildLifeOsActions } from "./life-os-actions";
// D42: Supabase-only sync runtime (Firebase removed). Internal aliases kept
// so internal variable names (syncEnabled etc.) remain readable.
import { cloudSyncConfigStatus, cloudSyncEnabled } from "../lib/sync-runtime";
import { hasPendingSyncItems, stamp } from "../lib/lifeOsMutations";
import {
  createLocalStateStorageKey,
  INDEXED_STATE_PLACEHOLDER_UPDATED_AT,
  isIndexedLocalStateReference,
  loadLocalState,
  loadLocalStateWithFallback,
} from "../lib/storage";
import { loadIndexedState } from "../lib/indexeddb-storage";
import { loadSyncRuntime } from "./life-os-sync-internals";
import { useLocalPersistence } from "./use-local-persistence";
import { useCloudSync } from "./use-cloud-sync";
import { useLifeOsSeeds } from "./use-life-os-seeds";
import type { LifeOsState } from "../types";

export function useLifeOs() {
  const syncEnabled = cloudSyncEnabled();
  const guestStorageKey = createLocalStateStorageKey("guest");
  const [state, setState] = useState<LifeOsState>(() =>
    syncEnabled ? loadLocalStateWithFallback(guestStorageKey) : loadLocalState(),
  );
  const syncConfigStatus = useMemo(() => cloudSyncConfigStatus(), []);
  const stateRef = useRef(state);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // ── Concerns extracted into focused hooks ─────────────────────────────────
  const { persistStateNow, schedulePersist, storageError } = useLocalPersistence();
  const { syncState, syncUser, lastPushAt, lastPullAt, retrySync, resetSignedOutGuestState } =
    useCloudSync({ syncEnabled, guestStorageKey, state, setState, stateRef, persistStateNow });
  const syncUid = syncUser?.uid;

  // ── Device-local persistence on change (local half; cloud push in useCloudSync) ──
  useEffect(() => {
    if (state.updatedAt === INDEXED_STATE_PLACEHOLDER_UPDATED_AT) {
      return;
    }
    const storageKey = syncEnabled
      ? createLocalStateStorageKey(syncUid ?? "guest")
      : createLocalStateStorageKey();
    schedulePersist(storageKey, state);
  }, [state, syncEnabled, syncUid, schedulePersist]);

  // Hydrate from IndexedDB if it holds a newer snapshot than what we loaded.
  useEffect(() => {
    const storageKey = syncEnabled ? createLocalStateStorageKey(syncUid ?? "guest") : createLocalStateStorageKey();
    let cancelled = false;
    void loadIndexedState(storageKey).then((indexedState) => {
      if (cancelled || !indexedState) {
        return;
      }
      const current = stateRef.current;
      const indexedNewer = new Date(indexedState.updatedAt).getTime() > new Date(current.updatedAt).getTime();
      if (indexedNewer && !hasPendingSyncItems(current)) {
        stateRef.current = indexedState;
        setState(indexedState);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [syncEnabled, syncUid]);

  // Multi-tab: adopt state written by ANOTHER tab of the same origin. The `storage`
  // event never fires in the tab that performed the write, so this cannot loop;
  // without it two open tabs silently overwrite each other's changes.
  useEffect(() => {
    const storageKey = syncEnabled ? createLocalStateStorageKey(syncUid ?? "guest") : createLocalStateStorageKey();
    const onStorage = (event: StorageEvent) => {
      if (event.key !== storageKey || !event.newValue) {
        return;
      }
      if (isIndexedLocalStateReference(event.newValue)) {
        void loadIndexedState(storageKey).then((incoming) => {
          if (!incoming || JSON.stringify(incoming) === JSON.stringify(stateRef.current)) {
            return;
          }
          stateRef.current = incoming;
          setState(incoming);
        });
        return;
      }
      // loadLocalState re-reads + migrates/normalizes, matching this tab's shape.
      const incoming = loadLocalState(storageKey);
      if (JSON.stringify(incoming) === JSON.stringify(stateRef.current)) {
        return;
      }
      stateRef.current = incoming;
      setState(incoming);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [syncEnabled, syncUid]);

  const mutate = useCallback((recipe: (current: LifeOsState) => LifeOsState) => {
    setState((current) => stamp(recipe(current)));
  }, []);

  const actions = useMemo(() => buildLifeOsActions(mutate, setState), [mutate, setState]);

  // One-time idempotent seeds (recurring sweep + default finance categories).
  useLifeOsSeeds(actions);

  return {
    state,
    actions,
    auth: {
      user: syncUser,
      signIn: (email: string, password: string) => loadSyncRuntime().then(({ signIn }) => signIn(email, password)),
      signUp: (email: string, password: string) => loadSyncRuntime().then(({ signUp }) => signUp(email, password)),
      resetPassword: (email: string) => loadSyncRuntime().then(({ resetPassword }) => resetPassword(email)),
      signOut: () => {
        resetSignedOutGuestState();
        return loadSyncRuntime().then(({ signOutOfSync }) => signOutOfSync());
      },
    },
    syncState,
    storageError,
    cloudSyncEnabled: syncEnabled,
    cloudSyncConfigStatus: syncConfigStatus,
    // Chẩn đoán đồng bộ (Settings): hàng đợi pending + mốc push/pull gần nhất.
    syncDiagnostics: {
      pendingPush: state.dirtyItems.length,
      pendingDelete: state.deletedItems.length,
      lastPushAt,
      lastPullAt,
    },
    // Re-subscribe + re-arm push ngay lập tức (nút "Thử lại" trong Settings).
    retrySync,
  };
}
